import React, { useState, useEffect, useCallback } from 'react';
import { Box, Grid, FormControlLabel, Switch, Typography } from '@mui/material';
import palette from 'src/theme/palette';
import { getTwoStepToggle, postSettingToggle } from 'src/ApiServices';
import { toast } from 'react-toastify';

const SecurityTab = ({ tabData = [], typeId }) => {
  const [isEnabled, setIsEnabled] = useState(false);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);

  const fetchTwoStepToggle = useCallback(async () => {
    try {
      setLoading(true);
      const response = await getTwoStepToggle();

      let isAuth;

      if (response && typeof response === 'object') {
        if ('is_auth' in response) {
          isAuth = response.is_auth;
        } else if ('data' in response && response.data && 'is_auth' in response.data) {
          isAuth = response.data.is_auth;
        }
      }

      setIsEnabled(isAuth == 1);
    } catch (error) {
      toast.error('Failed to load security settings');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchTwoStepToggle();
  }, [fetchTwoStepToggle]);

  const handleToggleChange = async (event) => {
    const newValue = event.target.checked;

    setUpdating(true);

    try {
      const payload = { is_auth: newValue ? 1 : 0 };
      const response = await postSettingToggle(payload);

      const serverValue = response.data?.is_auth === 1;
      setIsEnabled(serverValue);

      toast.success(
        serverValue
          ? 'Two-step verification has been enabled successfully!'
          : 'Two-step verification has been disabled.'
      );
    } catch (error) {
      toast.error('Failed to update two-step verification setting');
    } finally {
      setUpdating(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography>Loading security settings...</Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Box
            sx={{
              p: 3,
              border: `1px solid ${palette.grey[300]}`,
              borderRadius: 2,
              display: 'flex',
              justifyContent: 'space-between',
              flexDirection: { xs: 'column', sm: 'row' },
              alignItems: { xs: 'flex-start', sm: 'center' }
            }}
          >
            <Box>
              <Typography variant="h6">Two-Step Verification</Typography>
              <Typography variant="body2" color="text.secondary">
                {isEnabled
                  ? 'Two-step verification is enabled.'
                  : 'Two-step verification is disabled. Enable for enhanced security.'}
              </Typography>

              {updating && (
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ mt: 1, display: 'block' }}
                >
                  Updating...
                </Typography>
              )}
            </Box>

            <FormControlLabel
              control={
                <Switch
                  checked={isEnabled}
                  onChange={handleToggleChange}
                  color="primary"
                  disabled={updating || loading}
                />
              }
              label={isEnabled ? 'ON' : 'OFF'}
            />
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};

export default SecurityTab;
